import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ensureLocalApi } from "../../localApi";
import { useServerConfig } from "../../rpc/serverState";
import { useQuotaClock } from "../../useQuotaClock";
import { Button } from "../ui/button";
import { limitsSettingsView } from "./LimitsSettings.logic";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "./settingsLayout";

function UsageBar({ label, bar }: { label: string; bar: { value: number; label: string } }) {
  return (
    <div className="flex items-center gap-3 pb-3.5 pt-3">
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={bar.value}
        aria-valuetext={bar.label}
        className="h-2 flex-1 overflow-hidden rounded-full bg-muted"
      >
        <div className="h-full rounded-full bg-primary" style={{ width: `${bar.value}%` }} />
      </div>
      <span className="w-14 text-right text-xs tabular-nums text-muted-foreground">
        {bar.label}
      </span>
    </div>
  );
}

export function LimitsSettingsPanel() {
  const { t, i18n } = useTranslation();
  const quotas = useServerConfig()?.quotas ?? null;
  const nowMs = useQuotaClock(quotas);
  const view = limitsSettingsView(quotas, i18n.resolvedLanguage ?? i18n.language, nowMs);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);
  const refresh = useCallback(async () => {
    setRefreshing(true);
    setFailed(false);
    try {
      await ensureLocalApi().server.refreshQuotas();
    } catch {
      setFailed(true);
    } finally {
      setRefreshing(false);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  return (
    <SettingsPageContainer>
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">{t("settings/usageIsSharedBy_alira5")}</p>
        <Button variant="outline" size="sm" disabled={refreshing} onClick={() => void refresh()}>
          {t("settings/refresh_qrq5g7")}
        </Button>
      </div>
      {failed && (
        <p role="alert" className="text-sm text-destructive">
          {t("settings/couldNotRefreshLimits_f4dx3v")}
        </p>
      )}
      {view === null ? (
        <SettingsSection title={t("settings/limits_xn7zw0")}>
          <SettingsRow title={t("settings/noQuotaDataIs_jrtz5f")} description={null} />
        </SettingsSection>
      ) : (
        <>
          {[
            { id: "agent", title: t("settings/agent_xd06t3"), rows: view.agent },
            { id: "ocr", title: t("settings/documentRecognition_l8ho2g"), rows: view.ocr },
          ].map((section) => (
            <SettingsSection key={section.id} title={section.title}>
              {section.rows.length === 0 ? (
                <SettingsRow title={t("settings/noQuotaDataIs_jrtz5f")} description={null} />
              ) : (
                section.rows.map((row) => (
                  <SettingsRow key={row.kind} title={row.title} description={row.renewal}>
                    <UsageBar label={`${section.title}: ${row.title}`} bar={row.bar} />
                  </SettingsRow>
                ))
              )}
            </SettingsSection>
          ))}
          {view.storage && (
            <SettingsSection title={t("settings/storage_qhz84h")}>
              <SettingsRow title={view.storage.label} description={null}>
                <UsageBar label={t("settings/storage_qhz84h")} bar={view.storage.bar} />
              </SettingsRow>
            </SettingsSection>
          )}
        </>
      )}
    </SettingsPageContainer>
  );
}
